import { FC, useEffect, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useTranslation } from '@frontend/i18n';
import {
  CheckboxField,
  Chip,
  NakedButton,
  PopoverDialog,
  SearchField,
  Text,
  useControlledField,
  usePopoverDialog,
} from '@frontend/design-system';
import {
  actionsContainerStyle,
  clearButtonStyle,
  getCheckboxListStyle,
  getTriggerButtonStyle,
  labelTextStyle,
  listItemStyle,
  multipleItemsLabelContainerStyle,
  noResultsContainerStyle,
  popoverContentStyle,
  searchFieldContainerStyle,
} from './checklist-field.styles';
import { canSelectAll, clearValues, hasSelection, selectAllValues, toggleValue } from './checklist-selection.util';

interface DropdownChecklistFieldProps {
  options: Array<{ label: string; value: string }>;
  fieldId: string;
  value: string[];
  onChange: (value: string[]) => void;
  onBlur: () => void;
  hasError: boolean;
}

const DropdownCheckboxOption: FC<{
  option: { label: string; value: string };
  fieldId: string;
  isChecked: boolean;
  onToggle: (optionValue: string, checked: boolean) => void;
}> = ({ option, fieldId, isChecked, onToggle }) => {
  const checkboxProps = useControlledField({
    type: 'checkbox',
    value: isChecked,
    onChange: (checked: boolean) => onToggle(option.value, checked),
  });

  return <CheckboxField {...checkboxProps} name={`${fieldId}-opt-${option.value}`} label={option.label} />;
};

export const DropdownChecklistField: FC<DropdownChecklistFieldProps> = ({
  options,
  fieldId,
  value,
  onChange,
  onBlur,
  hasError,
}) => {
  const { t } = useTranslation('digital-forms-experience');
  const [searchValue, setSearchValue] = useState('');
  const [optionsToShow, setOptionsToShow] = useState<Array<{ label: string; value: string }>>([]);
  const { getTriggerProps, getDialogProps } = usePopoverDialog<HTMLButtonElement>({
    placement: 'bottom',

    middlewareOptions: {
      size: {
        apply({ elements, rects }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      },
    },
  });

  const searchFieldProps = useControlledField({
    type: 'text',
    value: searchValue,
    onChange: (val: string) => setSearchValue(val),
  });

  // Filter options based on search value
  useEffect(() => {
    if (searchValue) {
      const filteredOptions = options?.filter(({ label }) => {
        if (!label) return false;
        return label.toLowerCase().includes(searchValue.toLowerCase());
      });
      setOptionsToShow(filteredOptions);
    } else {
      setOptionsToShow(options);
    }
  }, [searchValue, options]);

  const handleToggle = (optionValue: string, checked: boolean) => {
    onChange(toggleValue(value, optionValue, checked));
    onBlur();
  };

  const handleSelectAll = () => {
    onChange(selectAllValues(value, optionsToShow));
    onBlur();
  };

  const handleClear = () => {
    onChange(clearValues(value, optionsToShow));
    onBlur();
  };

  const showSelectAll = canSelectAll(optionsToShow.length);
  const showClear = hasSelection(value, optionsToShow);

  const getLabel = () => {
    const MAX_CHIPS_TO_SHOW = 10;

    const selectedValues = value || [];
    if (selectedValues.length === 0) {
      return <Text css={labelTextStyle}>{t('Select')}</Text>;
    }

    const chips = selectedValues.slice(0, MAX_CHIPS_TO_SHOW).map((selectedValue) => {
      const selectedLabel = options.find((option) => option.value === selectedValue)?.label ?? selectedValue;

      return (
        <Chip key={selectedValue} variant='outline'>
          {selectedLabel}
        </Chip>
      );
    });

    return (
      <div css={multipleItemsLabelContainerStyle}>
        {chips}
        {selectedValues.length > MAX_CHIPS_TO_SHOW && (
          <Text size='medium' css={labelTextStyle}>
            +{selectedValues.length - MAX_CHIPS_TO_SHOW} {t('more')}
          </Text>
        )}
      </div>
    );
  };

  return (
    <>
      <NakedButton css={getTriggerButtonStyle(hasError)} {...getTriggerProps()}>
        {getLabel()}
      </NakedButton>

      <PopoverDialog {...getDialogProps()} css={popoverContentStyle}>
        <div css={searchFieldContainerStyle}>
          <SearchField {...searchFieldProps} name={`${fieldId}-search`} placeholder={t('Search...')} />
          {optionsToShow.length > 0 && (showSelectAll || showClear) && (
            <div css={actionsContainerStyle}>
              {showSelectAll && (
                <NakedButton onClick={handleSelectAll}>
                  <Text weight='semibold'>{t('Select All')}</Text>
                </NakedButton>
              )}
              {showClear && (
                <NakedButton css={clearButtonStyle} onClick={handleClear}>
                  <Text weight='semibold'>{t('Clear')}</Text>
                </NakedButton>
              )}
            </div>
          )}
        </div>

        {optionsToShow.length === 0 ? (
          <div css={noResultsContainerStyle}>
            <Text>{t('No results found')}</Text>
          </div>
        ) : (
          <div css={getCheckboxListStyle(optionsToShow.length)}>
            <Virtuoso
              data={optionsToShow}
              itemContent={(_i, option) => {
                const isChecked = (value || []).includes(option.value);

                return (
                  <div key={`${fieldId}-opt-${option.value}`} css={listItemStyle}>
                    <DropdownCheckboxOption
                      option={option}
                      fieldId={fieldId}
                      isChecked={isChecked}
                      onToggle={handleToggle}
                    />
                  </div>
                );
              }}
            />
          </div>
        )}
      </PopoverDialog>
    </>
  );
};
